import {
  Context,
  Dispatch,
  memo,
  MemoExoticComponent,
  Provider,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import styled from 'styled-components';
import {Form, Field, FieldMetaState} from 'react-final-form';

import FormField from '~/shared/components/FormField';
import useFormAutoFocus from '~/shared/hooks/useFormAutoFocus';
import {
  FieldWrapper,
  FormWrapper,
  HeaderText as SubHeaderText,
  LoginFormFieldsWrapper,
  BlackLinkButton,
} from '~/shared/components/loginStyled';
import useCanResendCode from '~/shared/utils/useCanResendCode';
import {getLocalizationService} from '~/shared/services/localisationService';
import {FlexCenter, FlexColumn} from '~/shared/theme/FlexLayout';
import {bodyBold, bodyRegular} from '~/shared/theme/typography';
import VerificationCodeInput from '~/shared/components/VerificationCodeInput';

import {VerificationFormContext, VerificationFormContextProps} from './context';
import GreenCheckCircleIcon from './greenCheckCircle.svg?react';

const AUTHENTICATION_CODE_FIELD_NAME = 'authenticationCode';

const StyledFieldWrapper = styled(FieldWrapper)`
  margin-top: 32px;
`;

const getVerificationInputError = (resErrorDescription: string | null, meta: FieldMetaState<string>): string | undefined => {
  if (!meta.submitFailed || meta.dirtySinceLastSubmit) {
    return;
  }

  return meta.error || resErrorDescription || undefined;
};

const codeValidation = (value: string) => {
  if (!value || value.length !== 5) {
    return 'activation_code_must_be_exact_5_digits';
  }

  return undefined;
};

const HeaderTexts = styled(FlexColumn)`
  gap: 8px;
  margin-bottom: 8px;
  padding: 0 16px;

  .verification-form-header {
    ${bodyRegular}
  }
`;

const StyledBlackLinkButton = styled(BlackLinkButton)`
  padding: 10px 0;
`;

const ResendCodeWrapper = styled(FlexCenter)`
  width: 100%;
  gap: 4px;
  height: 24px;
`;

const ReSendCodeIndication = styled.label.attrs({
  role: 'alert',
})`
  ${bodyBold}
  color: ${({theme}) => theme.colors.contentPositive};
  text-align: center;
`;

const HeaderText = styled(SubHeaderText)`
  ${bodyBold}
`;

interface IVerificationForm {
  onReSendCode: () => void;
  onSubmit: ({authenticationCode}: {authenticationCode?: string}) => void;
  headerText?: string;
  subHeaderText: ReactNode;
  resErrors: string | null;
  setResErrors: Dispatch<string | null>;
  toUpdatePhoneNumberScreen?: () => void;
  isGov?: boolean;
  className?: string;
}

const VerificationForm = ({
  onReSendCode,
  onSubmit,
  headerText,
  subHeaderText,
  resErrors: resErrorFromServer,
  setResErrors,
  toUpdatePhoneNumberScreen,
  isGov,
  className,
}: IVerificationForm) => {
  const {t} = getLocalizationService();
  const [isTriedReSendingCode, setIsTriedReSendingCode] = useState<boolean | null>(false);
  const {isResendButtonDisabled, setIsResendButtonDisabled} = useCanResendCode();
  const [isInputDisabled, setIsInputDisabled] = useState(false);

  const resetCode = () => {
    if (isResendButtonDisabled) {
      return;
    }

    setIsTriedReSendingCode(true);
    onReSendCode();
    setIsResendButtonDisabled(true);
  };

  const onSubmitHandler = useCallback(async ({authenticationCode}: {authenticationCode?: string}) => {
    try {
      setIsInputDisabled(true);
      await onSubmit({authenticationCode});
    } catch (err) {
      /**
       * In order to display an error after unsuccessful form submission we rely on `react-final-form` input's
       * meta-data. In order to get the meta.submitFailed state we need to trigger the internal Error detection of the
       * `react-final-form`. In case of an error `react-final-form` expects to receive a custom object (not instanceof
       * JS Error).
       * Links to docs:
       *    https://final-form.org/docs/final-form/types/FormApi#submit
       *    https://final-form.org/docs/react-final-form/types/FieldRenderProps#metasubmitfailed
       *
       * Our custom error structure is presented as following - it should be an object with the input's field name as
       * key and the error that is relevant to this specific field. This structure can scale in case we will add
       * additional fields and will have to add additional errors as well.
       * Example:
       *  err - {authenticationCode: undefined, password: {[SOME_ERROR]}}
       *  JSX - <input name="authenticationCode"> (no error)
       *        <input name="password"> (has error)
       */
      return {[AUTHENTICATION_CODE_FIELD_NAME]: err};
    } finally {
      setIsInputDisabled(false);
    }
  }, [onSubmit]);

  const {userError, isRequireCaptcha} = useContext(VerificationFormContext);
  const resErrorDescription = useMemo(() => userError?.message || resErrorFromServer, [resErrorFromServer, userError]);
  const formWrapRef = useFormAutoFocus();
  return (
    <div className={className}>
      <Form
        onSubmit={onSubmitHandler}
        render={({handleSubmit}) => (
          <FormWrapper onSubmit={handleSubmit} ref={formWrapRef}>
            {!isGov && (
              <HeaderTexts>
                <HeaderText className="verification-form-email">{headerText}</HeaderText>
                <SubHeaderText className="verification-form-header" id="modal-title">{subHeaderText}</SubHeaderText>
              </HeaderTexts>
            )}
            <LoginFormFieldsWrapper>
              <StyledFieldWrapper isGov={isGov}>
                <Field
                  name={AUTHENTICATION_CODE_FIELD_NAME}
                  validate={codeValidation}
                  render={({meta, input}) => (
                    <VerificationCodeInput
                      onChange={code => {
                        input.onChange(code);
                        if (!resErrorDescription) {
                          return;
                        }
                        setIsTriedReSendingCode(null);
                        setResErrors(null);
                      }}
                      onComplete={() => handleSubmit()}
                      error={getVerificationInputError(resErrorDescription, meta)}
                      disabled={isInputDisabled}
                    />
                  )}
                />
              </StyledFieldWrapper>
              {/* @ts-expect-error TODO: refactor FormField to TS*/}
              {isRequireCaptcha && <FormField name="reCaptchaResponse" type="recaptcha" />}
            </LoginFormFieldsWrapper>
          </FormWrapper>
        )}
      />
      <FlexColumn>
        {toUpdatePhoneNumberScreen && (
          <StyledBlackLinkButton isGov={isGov} onClick={toUpdatePhoneNumberScreen}>{t('update_phone_number')}</StyledBlackLinkButton>
        )}
        <StyledBlackLinkButton className="resend-code-button" isGov={isGov} onClick={resetCode} isDisabled={isResendButtonDisabled}>
          {t('i_did_not_receive_send_again')}
        </StyledBlackLinkButton>
      </FlexColumn>
      <ResendCodeWrapper>
        {isResendButtonDisabled && isTriedReSendingCode && (
          <>
            <GreenCheckCircleIcon />
            <ReSendCodeIndication>
              {t('we_sent_you_a_new_code')}
            </ReSendCodeIndication>
          </>
        )}
      </ResendCodeWrapper>
    </div>
  );
};

const PureVerificationForm = memo(VerificationForm) as MemoExoticComponent<
  (props: IVerificationForm) => JSX.Element | null
> & {
  Provider: Provider<VerificationFormContextProps>;
  Context: Context<VerificationFormContextProps>;
};

PureVerificationForm.Context = VerificationFormContext;
PureVerificationForm.Provider = VerificationFormContext.Provider;

export default PureVerificationForm;
